/**
 * @file lib/providerIdentity.js
 * @description How an agency screen names a provider (owner 2026-10-23, from David's privacy review). One server rule
 *   decides: the full name and photo once the provider has applied to, accepted an invite from, or messaged this
 *   agency; "First L." and no photo before that (RPC agency_provider_display_names, built on
 *   _provider_revealed_to_agency). Nothing the agency does reveals a provider, and the masking happens on the server,
 *   never here. Use this for any agency-side list of providers the agency contacted first (invites, threads it started).
 * @module lib/providerIdentity
 */
import { supabase } from "./supabase";

/**
 * Display names for providers, as the calling agency may see them.
 * @param {string[]} ids provider ids (at most 200 are looked up)
 * @returns {Promise<Object<string, { name: string|null, avatarUrl: string|null, shared: boolean }>>} keyed by id;
 *   ids the agency has no contact with are left out. Errors return {} (callers fall back to a generic label).
 */
export async function loadProviderNamesForAgency(ids) {
  const unique = [...new Set((ids || []).filter(Boolean))].slice(0, 200);
  if (!unique.length) return {};
  try {
    const { data, error } = await supabase.rpc("agency_provider_display_names", { p_ids: unique });
    if (error || !Array.isArray(data)) return {};
    return Object.fromEntries(data.map((r) => [r.id, { name: r.display_name || null, avatarUrl: r.avatar_url || null, shared: r.identity_shared === true }]));
  } catch {
    return {};
  }
}
